import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Target, Trophy } from 'lucide-react';
import {
  ParsedScoreboard,
  ScoreboardLeg,
  ScoreboardVisit,
} from '../../services/throwByThrowParser';

const ACCENT = '#3fced1';

interface MatchScoreboardProps {
  scoreboard: ParsedScoreboard;
  homeName: string;
  awayName: string;
  isLive: boolean;
}

/**
 * Leg-für-Leg-Scoreboard (throw-by-throw) für die Match-Detailseite.
 * - Leg-Selector als Chips (laufendes Leg pulsiert, Auto-Sprung auf neues Leg,
 *   solange der User nicht manuell ein anderes Leg gewählt hat)
 * - Laufendes Leg: große Restpunkte-Anzeige
 * - Aufnahmen-Tabelle (Heim links, Auswärts rechts) mit 140+/180-Highlights
 * - Fertige Legs: Sieger + Checkout-Zeile
 */
const MatchScoreboard: React.FC<MatchScoreboardProps> = ({
  scoreboard,
  homeName,
  awayName,
  isLive,
}) => {
  const { legs, currentLeg } = scoreboard;
  const [selectedIndex, setSelectedIndex] = useState<number>(Math.max(0, legs.length - 1));
  const userPickedRef = useRef(false);
  const prevLegCountRef = useRef(legs.length);

  // Auto-Sprung auf das neueste Leg, wenn ein neues beginnt (außer der User
  // hat manuell ein älteres Leg ausgewählt)
  useEffect(() => {
    if (legs.length !== prevLegCountRef.current) {
      prevLegCountRef.current = legs.length;
      if (!userPickedRef.current) {
        setSelectedIndex(Math.max(0, legs.length - 1));
      }
    }
    // Index absichern, falls Blöcke serverseitig neu aufgebaut wurden
    if (selectedIndex > legs.length - 1) {
      setSelectedIndex(Math.max(0, legs.length - 1));
      userPickedRef.current = false;
    }
  }, [legs.length, selectedIndex]);

  const selectedLeg: ScoreboardLeg | undefined = legs[selectedIndex];

  const rows = useMemo(
    () => (selectedLeg ? buildVisitRows(selectedLeg.visits) : []),
    [selectedLeg]
  );

  if (legs.length === 0) return null;

  const pickLeg = (index: number) => {
    userPickedRef.current = index !== legs.length - 1;
    setSelectedIndex(index);
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      {/* Leg-Selector */}
      <div className="flex gap-1.5 overflow-x-auto px-4 pt-4 pb-3 border-b border-zinc-800 [-webkit-overflow-scrolling:touch]">
        {legs.map((leg, i) => {
          const isSelected = i === selectedIndex;
          const isRunning = !leg.isFinished;
          return (
            <button
              key={`${leg.legNumber}-${i}`}
              type="button"
              onClick={() => pickLeg(i)}
              aria-pressed={isSelected}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                isSelected
                  ? 'bg-[#3fced1] text-black'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              {isRunning && (
                <span className="relative flex h-1.5 w-1.5">
                  <span
                    className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                      isSelected ? 'bg-black/60' : 'bg-red-400'
                    }`}
                  />
                  <span
                    className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                      isSelected ? 'bg-black' : 'bg-red-500'
                    }`}
                  />
                </span>
              )}
              Leg {leg.legNumber}
            </button>
          );
        })}
      </div>

      {selectedLeg && (
        <div className="p-4 sm:p-5">
          {/* Leg-Status-Kopf */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {selectedLeg.isFinished ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 border border-zinc-700 px-2.5 py-1 text-[11px] font-bold text-gray-300">
                <Trophy size={11} color={ACCENT} />
                {selectedLeg.winner === 1
                  ? homeName
                  : selectedLeg.winner === 2
                  ? awayName
                  : 'Beendet'}
                {selectedLeg.checkout !== null && (
                  <span style={{ color: ACCENT }}>· Checkout {selectedLeg.checkout}</span>
                )}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 border border-red-500/30 px-2.5 py-1 text-[11px] font-bold text-red-400 uppercase tracking-wider">
                Leg läuft
              </span>
            )}
            {selectedLeg.starter !== null && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/70 px-2.5 py-1 text-[11px] text-gray-400">
                <Target size={11} className="shrink-0" style={{ color: ACCENT }} />
                Anwurf: {selectedLeg.starter === 1 ? homeName : awayName}
              </span>
            )}
            {selectedLeg.matchScore && (
              <span className="ml-auto text-[11px] text-gray-500 tabular-nums">
                Stand danach: {selectedLeg.matchScore.home}:{selectedLeg.matchScore.away}
              </span>
            )}
          </div>

          {/* Live-Restpunkte prominent (nur laufendes Leg) */}
          {!selectedLeg.isFinished && (
            <div className="grid grid-cols-2 gap-3 mb-5">
              <RemainingCard
                name={homeName}
                remaining={selectedLeg.homeRemaining}
                pulse={isLive}
              />
              <RemainingCard
                name={awayName}
                remaining={selectedLeg.awayRemaining}
                pulse={isLive}
              />
            </div>
          )}

          {/* Aufnahmen-Tabelle */}
          {rows.length > 0 ? (
            <div>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 pb-2 border-b border-zinc-800 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <span className="truncate">{homeName}</span>
                <span className="w-8 text-center">#</span>
                <span className="truncate text-right">{awayName}</span>
              </div>
              <div className="divide-y divide-zinc-800/60">
                {rows.map((row, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-1.5"
                  >
                    <VisitCell visit={row.home} align="left" />
                    <span className="w-8 text-center text-[11px] text-zinc-600 tabular-nums">
                      {row.round}
                    </span>
                    <VisitCell visit={row.away} align="right" />
                  </div>
                ))}
              </div>
              {/* Checkout-Abschlusszeile für fertige Legs */}
              {selectedLeg.isFinished && selectedLeg.checkout !== null && (
                <div
                  className={`mt-2 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold ${
                    selectedLeg.winner === 2 ? 'justify-end' : ''
                  }`}
                  style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
                >
                  <Trophy size={12} />
                  {selectedLeg.winner === 1 ? homeName : awayName} checkt {selectedLeg.checkout}
                </div>
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-gray-500">
              Noch keine Aufnahmen in diesem Leg.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Bausteine
// ============================================

const RemainingCard: React.FC<{ name: string; remaining: number; pulse: boolean }> = ({
  name,
  remaining,
  pulse,
}) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 text-center">
    <p className="text-[11px] uppercase tracking-wider text-gray-500 truncate">{name}</p>
    <p
      className={`mt-1 text-3xl sm:text-4xl font-extrabold tabular-nums leading-none ${
        remaining <= 170 ? '' : 'text-white'
      }`}
      style={remaining <= 170 ? { color: ACCENT } : undefined}
      title={remaining <= 170 ? 'Checkout-Reichweite' : undefined}
    >
      {remaining}
    </p>
    {pulse && <span className="sr-only">Live-Restpunkte</span>}
  </div>
);

const VisitCell: React.FC<{ visit: ScoreboardVisit | null; align: 'left' | 'right' }> = ({
  visit,
  align,
}) => {
  if (!visit) return <span />;
  const is180 = visit.thrown === 180 || visit.highlight === '180';
  const isHighlight = is180 || (visit.thrown !== null && visit.thrown >= 140) || !!visit.highlight;
  return (
    <span
      className={`flex items-baseline gap-2 min-w-0 ${
        align === 'right' ? 'flex-row-reverse' : ''
      }`}
    >
      <span
        className={`text-sm font-bold tabular-nums ${
          is180 ? '' : isHighlight ? '' : 'text-gray-200'
        }`}
        style={isHighlight ? { color: is180 ? '#f59e0b' : ACCENT } : undefined}
      >
        {visit.thrown !== null ? visit.thrown : '–'}
      </span>
      <span className="text-[11px] text-zinc-500 tabular-nums">Rest {visit.remaining}</span>
    </span>
  );
};

interface VisitRow {
  round: number;
  home: ScoreboardVisit | null;
  away: ScoreboardVisit | null;
}

/** Aufnahmen in Zeilen gruppieren: Heim + Auswärts derselben Runde nebeneinander. */
function buildVisitRows(visits: ScoreboardVisit[]): VisitRow[] {
  const rows: VisitRow[] = [];
  let current: VisitRow | null = null;
  for (const visit of visits) {
    const slot = visit.team === 1 ? 'home' : 'away';
    if (!current || current[slot] !== null) {
      current = { round: rows.length + 1, home: null, away: null };
      rows.push(current);
    }
    current[slot] = visit;
  }
  return rows;
}

export default MatchScoreboard;
